export function useFacebookPixel() {
  const { $fbq, $versions, $cbqTrack, $cbqInit, $logger } = useNuxtApp();
  const route = useRoute();
  const store = useKegelAnalyticStore();
  const API = useKegelApi();
  const { getHashedText } = useHelpers();
  let PIXEL_ID = '610922901428415';
  let additionalPixelIds = ['1713039456269840', '588422620978834'];

  if ($versions.value.landing === '16' || $versions.value.landing === '17') {
    PIXEL_ID = '1635665980452739';
    additionalPixelIds = [];
  }

  if (route.query.pixelId) {
    PIXEL_ID = route.query.pixelId as string;
  }

  const defaultEventData = computed(() => {
    return {
      ge: 'm',
      ...store.userData,
    };
  });

  const cbqInitData = computed(() => {
    const data = { ...defaultEventData.value } as Record<string, unknown>;

    if (store.userEmail) {
      data.em = store.sha256UserEmail;
    }

    return data;
  });

  function ensureFbp(): void {
    const existing = document.cookie.match(/_fbp=([^;]+)/)?.[1];
    if (existing) return;
    const fbp = `fb.1.${Date.now()}.${Math.floor(Math.random() * 1e9)}`;
    document.cookie = `_fbp=${fbp}; max-age=7776000; path=/; SameSite=Lax`;
  }

  const initPixel = async (): Promise<void> => {
    if (store.userEmail && import.meta.client) {
      store.sha256UserEmail = await getHashedText(store.userEmail);
    }
    if (import.meta.client) {
      ensureFbp();
      $cbqInit(cbqInitData.value);
      ($fbq as any)?.('init', PIXEL_ID, { em: store.sha256UserEmail, ...defaultEventData.value });
      additionalPixelIds.forEach((pixelId: string): void => {
        ($fbq as any)?.('init', pixelId, { em: store.sha256UserEmail, ...defaultEventData.value });
      });
    }
  };

  const fbqTrack = (event: string): void => {
    const clientEvents = ['PageView'];
    if (!import.meta.client) {
      return;
    }

    if (clientEvents.includes(event)) {
      ($fbq as any)?.('track', event, defaultEventData.value);
      if (event === 'PageView') {
        $cbqTrack('track', 'PageView');
      }
    } else {
      sendEventToCapi(event);
    }
  };

  async function sendEventToCapi(event: string): Promise<void> {
    const fbp = useCookie('_fbp').value;
    const fbc = useCookie('_fbc').value || store.analyticsAttributions?.fbclid;

    const data = {
      fbp,
      fbc,
      ge: 'm',
      client_user_agent: navigator.userAgent,
      external_id: store.userUuid,
      ...(store.userEmail ? { em: store.userEmail } : {}),
    };

    try {
      await Promise.allSettled([
        API.sendFbEvent('610922901428415', { event_name: event, user_data: data }),
        API.sendFbEvent('1713039456269840', { event_name: event, user_data: data }),
        API.sendFbEvent('588422620978834', { event_name: event, user_data: data }),
      ]);
    } catch (error: unknown) {
      $logger.note({
        category: 'facebookPixel',
        message: 'sending pixel events to our api failed',
        level: 'warning',
        cause: error,
      });
    }
  }

  const pixelAddUserData = ({ em }: { em?: string }): void => {
    $cbqInit(cbqInitData.value);
    ($fbq as any)?.('init', PIXEL_ID, { em });
    additionalPixelIds.forEach((pixelId: string): void => {
      ($fbq as any)?.('init', pixelId, { em });
    });
  };

  const fbqCustomTrack = (event: string): void => {
    if (import.meta.client) {
      ($fbq as any)?.('trackSingleCustom', PIXEL_ID, event);
    }
  };

  return {
    fbqTrack,
    initPixel,
    fbqCustomTrack,
    pixelAddUserData,
    pixelId: PIXEL_ID,
  };
}
